import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { EntityRef } from '../../components/EntityRef'
import { Icon } from '../../components/icons/Icon'
import { InlineAlert } from '../../components/InlineAlert'
import { StatusPill } from '../../components/StatusPill'
import { TimelinePanel } from '../../components/timeline/TimelinePanel'
import { HelpBalloon } from '../../help/HelpBalloon'
import type { HelpContentId } from '../../help/helpContent'
import { WidgetErrorBoundary } from '../insights/widgets/WidgetErrorBoundary'
import { getCase, listCaseProgramFilings } from '../../api/cases'
import { ApiError } from '../../api/problem'
import { label } from '../../lib/labels'
import { useCaseContexts } from '../tasks/useCaseContexts'
import type { CaseKind, CaseView } from '../../types/case'
import type { ProgramFilingDetailView } from '../../types/programFilingDetail'
import { AuthorityPanel } from './AuthorityPanel'
import { CaseAnalyticsContainer } from './analytics/CaseAnalyticsContainer'
import type { CaseAnalyticsTab } from './analytics/useCaseAnalytics'
import { CreditPanel } from './CreditPanel'
import { DossierPanel } from './DossierPanel'
import { EligibilityPanel } from './EligibilityPanel'
import { EvidencePanel } from './EvidencePanel'
import { MonetizationPanel } from './MonetizationPanel'
import { OverviewPanel } from './OverviewPanel'
import { SpendPanel } from './SpendPanel'
import { TasksPanel } from './TasksPanel'
import { TeamPanel } from './TeamPanel'

type CaseTab =
  | 'overview'
  | 'spend'
  | 'evidence'
  | 'credit'
  | 'analytics'
  | 'monetization'
  | 'dossier'
  | 'eligibility'
  | 'authority'
  | 'team'
  | 'tasks'
  | 'timeline'

/**
 * Data-driven tab list (docs/design/IB-49-crm-detail-timeline-design.md §6) — the coordination-safe
 * shape for IB-49 (Timeline) and IB-51 (Team) both adding an independent tab to this same screen:
 * whichever story merges second rebases by adding its one row here, no logic reconciliation. Adding
 * a future tab is a one-line addition, never a second `if (kind === ...)` branch.
 *
 * Dossier/Spend/Monetization stay film-only (film-specific surfaces, data-model §3.4/§12.3/§12.5);
 * Overview/Evidence/Credit/Team/Tasks/Timeline are universal — evidence and certified credit are
 * not film-only concepts, "a case team is not a film-only concept" (IB-51), a Task/work-item is
 * not a film-only concept either (module.task), and Timeline is FR-M5.2's universal per-entity
 * contract.
 *
 * Eligibility (IB-11, FR-M8.1-8.3) is universal — `EligibilityResult`/`JudgmentGate` are keyed by
 * `program_filing_id` alone, no `kind`-specific concept, so it is on for both `rnd` and `film`
 * (GA demo lifecycle-tabs pass). Authority (IB-14, ADR-0026) hangs off `ProgramFiling` too, but this
 * pass scopes the tab to `film` cases only, mirroring Dossier/Spend/Monetization's existing
 * film-only posture on this same screen — an R&D-side Authority tab is a future, not-yet-scoped
 * addition, not a claim that `AuthorityInteraction` itself is structurally film-only.
 */
const TABS: { key: CaseTab; label: string; enabledFor: CaseKind[] }[] = [
  { key: 'overview', label: 'Overview', enabledFor: ['rnd', 'film'] },
  { key: 'spend', label: 'Spend', enabledFor: ['film'] },
  { key: 'evidence', label: 'Evidence', enabledFor: ['rnd', 'film'] },
  { key: 'credit', label: 'Credit', enabledFor: ['rnd', 'film'] },
  { key: 'analytics', label: 'Analytics', enabledFor: ['rnd', 'film'] },
  { key: 'eligibility', label: 'Eligibility', enabledFor: ['rnd', 'film'] },
  { key: 'monetization', label: 'Monetization', enabledFor: ['film'] },
  { key: 'dossier', label: 'Dossier', enabledFor: ['film'] },
  { key: 'authority', label: 'Authority', enabledFor: ['film'] },
  { key: 'team', label: 'Team', enabledFor: ['rnd', 'film'] },
  { key: 'tasks', label: 'Tasks', enabledFor: ['rnd', 'film'] },
  { key: 'timeline', label: 'Timeline', enabledFor: ['rnd', 'film'] },
]

/** Section-level help registry key for each non-Overview tab (Overview carries its own in-panel
 * headings/balloons — see `OverviewPanel.tsx` — so it is deliberately absent here). */
const TAB_HELP_ID: Partial<Record<CaseTab, HelpContentId>> = {
  spend: 'tab.spend',
  evidence: 'tab.evidence',
  credit: 'tab.credit',
  eligibility: 'tab.eligibility',
  monetization: 'tab.monetization',
  dossier: 'tab.dossier',
  authority: 'tab.authority',
  team: 'tab.team',
  tasks: 'tab.tasks',
  timeline: 'tab.timeline',
}

/** The heading row every non-Overview tabpanel renders above its content (roadmap #12) — sits
 * outside `WidgetErrorBoundary` so the section's own explanation is still visible even if that
 * tab's content throws. */
function TabSectionHeading({ tab, label: headingLabel }: { tab: CaseTab; label: string }) {
  const helpId = TAB_HELP_ID[tab]
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)', marginBottom: 'var(--iw-space-3)' }}>
      <h2 style={{ margin: 0, fontSize: 'var(--iw-size-md)' }}>{headingLabel}</h2>
      {helpId ? <HelpBalloon id={helpId} /> : null}
    </div>
  )
}

export interface CaseDetailScreenProps {
  /** Lets `/cases/:id/dossier` land straight on the Dossier tab (IB-159) — the tab is reached via
   * an ordinary React Router `element` prop, not a second screen component or a route redirect. */
  initialTab?: CaseTab
}

/**
 * Case detail shell (IB-53 Slice A AC4, extended IB-159/IB-51/IB-49/GA-demo-v4/lifecycle-tabs) —
 * header + an enriched Overview (summary tiles + related ProgramFilings), a film-only Spend tab
 * (qualified spend lines + guarded-adjustment history), a universal Evidence tab, a universal
 * Credit tab (certified `CreditInstrument` + pinned final `ComputationRun`), a universal Eligibility
 * tab (`EligibilityResult` verdicts + authored `JudgmentGate`s, IB-11), a film-only Monetization tab
 * (credit `TransferTxn`), a film-only Dossier tab, a film-only Authority tab (`AuthorityInteraction`
 * status history + linked `SubmissionPackage`, IB-14), a universal Team tab (IB-51), and a universal
 * Timeline tab (IB-49). Every non-trivial tab body is wrapped in a `WidgetErrorBoundary` (the same
 * containment pattern `DashboardScreen` uses) so one tab's unexpected render failure never blanks
 * the rest of this screen.
 */
export function CaseDetailScreen({ initialTab = 'overview' }: CaseDetailScreenProps = {}) {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [caseView, setCaseView] = useState<CaseView | null>(null)
  const [filings, setFilings] = useState<ProgramFilingDetailView[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState<CaseTab>(initialTab)

  useEffect(() => {
    if (!id) return
    let cancelled = false
    setLoading(true)
    setError(null)
    Promise.all([getCase(id), listCaseProgramFilings(id)])
      .then(([c, filingsPage]) => {
        if (cancelled) return
        setCaseView(c)
        setFilings(filingsPage.items)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not load this case.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [id])

  const caseContexts = useCaseContexts([id ?? null])
  const caseContext = id ? caseContexts.get(id) : undefined
  const caseLabel = caseContext
    ? [caseContext.accountName, caseContext.caseLabel].filter(Boolean).join(' · ') || caseContext.caseLabel
    : null

  return (
    <div style={{ padding: 'var(--iw-space-5)', display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}>
      <div style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
        <Link to="/cases" style={{ color: 'var(--iw-text-muted)' }}>
          Cases
        </Link>{' '}
        › <EntityRef kind="case" id={id} name={caseLabel} />
      </div>

      {error ? (
        <InlineAlert
          severity="error"
          action={
            <button type="button" className="iw-btn iw-btn--sm" onClick={() => navigate(0)}>
              Try again
            </button>
          }
        >
          {error}
        </InlineAlert>
      ) : loading ? (
        <p style={{ color: 'var(--iw-text-muted)' }}>Loading case…</p>
      ) : caseView ? (
        <CaseDetailBody caseView={caseView} filings={filings} tab={tab} setTab={setTab} />
      ) : null}
    </div>
  )
}

/** Extracted so `applicableTabs` can be computed from a definitely-non-null `caseView` (IB-51). */
function CaseDetailBody({
  caseView,
  filings,
  tab,
  setTab,
}: {
  caseView: CaseView
  filings: ProgramFilingDetailView[]
  tab: CaseTab
  setTab: (t: CaseTab) => void
}) {
  const applicableTabs = TABS.filter((t) => t.enabledFor.includes(caseView.kind))

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-3)', flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--iw-accent)' }}>
          <Icon name={caseView.kind === 'film' ? 'film' : 'briefcase'} size={22} />
        </span>
        <h1 style={{ margin: 0, fontSize: 'var(--iw-size-xl)' }}>
          {label('caseKind', caseView.kind)} case
        </h1>
        <StatusPill
          variant={caseView.overallStatus === 'closed' ? 'muted' : 'success'}
          label={caseView.overallStatus === 'closed' ? 'Closed' : 'Open'}
        />
        {caseView.kind === 'film' && caseView.productionId ? (
          <>
            <span style={{ flex: 1 }} />
            <Link to={`/productions/${caseView.productionId}`} className="iw-btn iw-btn--primary">
              Open production
            </Link>
          </>
        ) : null}
      </div>

      {/* Tabs (IB-159, ≤2 clicks per CLAUDE.md §4 IA). The tablist shell is ungated from any
          `kind==='film'` condition -- it shows whenever there is more than one applicable tab for
          this case's kind (always true: Overview/Evidence/Credit/Team/Timeline alone are five).
          Client-side tab state, not a route change, so switching tabs never remounts a panel and
          loses its in-memory state mid-session. */}
      {applicableTabs.length > 1 ? (
        <div role="tablist" aria-label="Case sections" style={{ display: 'flex', gap: 'var(--iw-space-2)', borderBottom: '1px solid var(--iw-border)', flexWrap: 'wrap' }}>
          {applicableTabs.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              className="iw-btn iw-btn--ghost"
              style={tab === t.key ? { borderBottom: '2px solid var(--iw-accent)', borderRadius: 0, color: 'var(--iw-accent)' } : { borderRadius: 0 }}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      ) : null}

      {tab === 'spend' && caseView.kind === 'film' ? (
        <div role="tabpanel" aria-label="Spend">
          <TabSectionHeading tab="spend" label="Spend" />
          <WidgetErrorBoundary title="Spend" icon="dollar-sign">
            {caseView.productionId ? (
              <SpendPanel productionId={caseView.productionId} />
            ) : (
              <p style={{ color: 'var(--iw-text-muted)' }}>No production linked to this case yet.</p>
            )}
          </WidgetErrorBoundary>
        </div>
      ) : tab === 'evidence' ? (
        <div role="tabpanel" aria-label="Evidence">
          <TabSectionHeading tab="evidence" label="Evidence" />
          <WidgetErrorBoundary title="Evidence" icon="archive">
            <EvidencePanel caseId={caseView.id} filings={filings} />
          </WidgetErrorBoundary>
        </div>
      ) : tab === 'credit' ? (
        <div role="tabpanel" aria-label="Credit">
          <TabSectionHeading tab="credit" label="Credit" />
          <WidgetErrorBoundary title="Credit" icon="dollar-sign">
            <CreditPanel filings={filings} />
          </WidgetErrorBoundary>
        </div>
      ) : tab === 'analytics' ? (
        <div role="tabpanel" aria-label="Analytics">
          <TabSectionHeading tab="analytics" label="Analytics" />
          <WidgetErrorBoundary title="Analytics" icon="bar-chart">
            <CaseAnalyticsContainer
              caseView={caseView}
              filings={filings}
              onNavigateTab={(t: CaseAnalyticsTab) => setTab(t)}
            />
          </WidgetErrorBoundary>
        </div>
      ) : tab === 'monetization' && caseView.kind === 'film' ? (
        <div role="tabpanel" aria-label="Monetization">
          <TabSectionHeading tab="monetization" label="Monetization" />
          <WidgetErrorBoundary title="Monetization" icon="dollar-sign">
            <MonetizationPanel filings={filings} />
          </WidgetErrorBoundary>
        </div>
      ) : tab === 'dossier' && caseView.kind === 'film' ? (
        <div role="tabpanel" aria-label="Dossier">
          <TabSectionHeading tab="dossier" label="Dossier" />
          <DossierPanel caseView={caseView} />
        </div>
      ) : tab === 'eligibility' ? (
        <div role="tabpanel" aria-label="Eligibility">
          <TabSectionHeading tab="eligibility" label="Eligibility" />
          <WidgetErrorBoundary title="Eligibility" icon="check-circle">
            <EligibilityPanel filings={filings} />
          </WidgetErrorBoundary>
        </div>
      ) : tab === 'authority' && caseView.kind === 'film' ? (
        <div role="tabpanel" aria-label="Authority">
          <TabSectionHeading tab="authority" label="Authority" />
          <WidgetErrorBoundary title="Authority" icon="landmark">
            <AuthorityPanel filings={filings} />
          </WidgetErrorBoundary>
        </div>
      ) : tab === 'team' ? (
        <div role="tabpanel" aria-label="Team">
          <TabSectionHeading tab="team" label="Team" />
          <TeamPanel caseId={caseView.id} />
        </div>
      ) : tab === 'tasks' ? (
        <div role="tabpanel" aria-label="Tasks">
          <TabSectionHeading tab="tasks" label="Tasks" />
          <WidgetErrorBoundary title="Tasks" icon="check-circle">
            <TasksPanel caseId={caseView.id} />
          </WidgetErrorBoundary>
        </div>
      ) : tab === 'timeline' ? (
        <div role="tabpanel" aria-label="Timeline">
          <TabSectionHeading tab="timeline" label="Timeline" />
          <TimelinePanel target={{ type: 'Case', id: caseView.id }} />
        </div>
      ) : (
        <div role="tabpanel" aria-label="Overview">
          <WidgetErrorBoundary title="Overview" icon="briefcase">
            <OverviewPanel caseView={caseView} filings={filings} />
          </WidgetErrorBoundary>
        </div>
      )}
    </>
  )
}
